import { subject as an } from '@casl/ability';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SortIcon from '@mui/icons-material/Sort';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, gridClasses } from '@mui/x-data-grid';
import ProductSelectDialog, {
  ProductSelectModeEnum,
} from 'components/ProductSelectDialog';
import PromptDialog from 'core/components/Dialogs/PromptDialog';
import EmptyMessage from 'core/components/EmptyMessage';
import SplitButton from 'core/components/SplitButton';
import {
  currencyFormatter,
  dateTimeFormatter,
  intentionallyFloatingPromiseReturn,
} from 'core/utils/commonUtils';
import { currencyCol, renderCellExpand } from 'core/utils/muiGridUtils';
import { getMeasureName, getTotalSum } from 'core/utils/productUtils';
import { SWR_ORDER_PROJECTS_ENDPOINT } from 'hooks/swr/useOrderProjects';
import useResponsive from 'hooks/useResponsive';
import { cloneDeep, first, isArray, isNil } from 'lodash';
import { useConfirm } from 'material-ui-confirm';
import { observer } from 'mobx-react-lite';
import { MeasureEnum } from 'models/enums/measureEnum';
import { useContext, useState } from 'react';
import * as orderProjectService from 'services/orderProjectService';
import StoreContext from 'stores/context/StoreContext';
import { mutate } from 'swr';
import ProductEditDialog from './ProductEditDialog';
import ProductOrderDialog from './ProductOrderDialog';
import ProductTags from './ProductTags';
import ProductsGridFooter from './ProductsGridFooter';
import Total from './Total';
import * as projectUtils from './orderProjectUtils';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import type { AbilitySubjectsStr } from 'core/permissions/defineAbilityFor';
import type { OrderProject, ProjectProduct } from 'models/order/orderProject';
import type { Product } from 'models/product/product';
import type { FC } from 'react';
import type { CreateOrUpdateOrderProjectRequest } from 'services/dtoModels/order/createOrUpdateOrderProjectRequest';

// TODO: Добавить loader на перемещение между активными/не активными, и изменение товаров
const OrderProjectEdit: FC<OrderProjectEditParams> = observer(
  ({ orderId, projects, project, onCopy }) => {
    const isSmScreen = useResponsive('down', 'sm');

    const { userStore } = useContext(StoreContext);

    const [renameLoading, setRenameLoading] = useState(false);

    const [renameDialogOpen, setRenameDialogOpen] = useState(false);
    const [productDialogOpen, setProductDialogOpen] = useState(false);
    const [productOrderDialogOpen, setProductOrderDialogOpen] = useState(false);
    const [editedProduct, setEditedProduct] = useState<ProjectProduct>();
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
      page: 0,
      pageSize: 25,
    });
    const [openProductSelectDialog, setOpenProductSelectDialog] = useState(false);
    const [productSelectDialogMode, setProductSelectDialogMode] = useState(
      ProductSelectModeEnum.Single
    );

    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const openMenu = Boolean(menuAnchorEl);

    const deletionConfirm = useConfirm();

    const sortedProducts = [...project.orderProducts].sort(
      (a, b) => a.positionIndex - b.positionIndex
    );

    const deleteProduct = async (product: ProjectProduct): Promise<void> => {
      await deletionConfirm({
        description: `Удалить "${product.name}"?`,
      })
        .then(
          async () =>
            await updateProjectSwr(async () => {
              const request = projectUtils.removeProduct(cloneDeep(project), product.id);

              if (isNil(request)) {
                throw new Error('Не удалось удалить товар');
              }

              await orderProjectService.deleteProductAsync(
                orderId,
                project.id,
                product.id
              );

              return request;
            })
        )
        .catch(() => false);
    };

    const updateProjectSwr = async (
      data: () => Promise<OrderProject>
    ): Promise<Array<OrderProject[] | undefined>> =>
      await mutate(
        (key) => isArray(key) && key[0] === SWR_ORDER_PROJECTS_ENDPOINT,
        data().then((p) => {
          const index = projects.findIndex((v) => project.id === v.id);
          const temp = cloneDeep(projects);
          temp.splice(index, 1, p);

          return temp;
        }),
        {
          rollbackOnError: true,
          populateCache: true,
          revalidate: false,
        }
      );

    const canEdit =
      userStore.ability?.can(
        'update',
        an<AbilitySubjectsStr, OrderProject>('OrderProject', project)
      ) ?? false;

    const handleSaveProductOrder = async (
      orderedProducts: ProjectProduct[]
    ): Promise<void> => {
      await updateProjectSwr(async () => {
        await orderProjectService.updateProductPositionsAsync(
          orderId,
          project.id,
          orderedProducts.map((p) => ({
            orderProductId: p.id,
            positionIndex: p.positionIndex,
          }))
        );

        const newProject = cloneDeep(project);
        newProject.orderProducts = orderedProducts;

        return newProject;
      });

      setProductOrderDialogOpen(false);
    };

    const columns: Array<GridColDef<ProjectProduct>> = [
      {
        field: 'name',
        headerName: 'Название',
        flex: 1,
        renderCell: (params) => (
          <Stack direction='row' alignItems='center'>
            <span>{params.value}</span>
            <ProductTags tags={params.row.tags} />
          </Stack>
        ),
      },
      {
        field: 'count',
        headerName: 'Количество',
        type: 'number',
        valueFormatter: (value, row) => `${+value} ${getMeasureName(row.measure, true)}`,
      },
      {
        field: 'price',
        headerName: 'Цена',
        ...currencyCol,
      },
      {
        field: 'markup',
        headerName: 'Наценка',
        valueGetter: (_, row) =>
          projectUtils.getTotalMarkupForUser([row], userStore.userData?.id ?? 0),
        renderCell: (params) => (
          <>
            {currencyFormatter.format(params.value.total)}
            {params.value.user > 0 && ` (${currencyFormatter.format(params.value.user)})`}
          </>
        ),
        sortComparator: (a, b) => a.total - b.total,
      },
      {
        field: 'totalSum',
        headerName: 'Стоимость',
        ...currencyCol,
      },
      {
        field: 'remark',
        headerName: 'Примечание',
        flex: 0.5,
        renderCell: renderCellExpand,
      },
      {
        field: 'positionIndex',
        headerName: '№',
        width: 80,
        type: 'number',
        valueGetter: (value: number) => value + 1,
        renderHeader: () => (
          <Typography variant='body2' fontWeight={500}>
            Порядок
            <br />
            печати
          </Typography>
        ),
        // renderCell: (params) => (
        //   <>
        //     {params.value}
        //     {canEdit && (
        //       <Tooltip title='Настроить порядок товаров'>
        //         <IconButton
        //           size='small'
        //           onClick={(e) => {
        //             e.stopPropagation();
        //             setProductOrderDialogOpen(true);
        //           }}>
        //           <SortIcon fontSize='small' />
        //         </IconButton>
        //       </Tooltip>
        //     )}
        //   </>
        // ),
      },
    ];

    if (canEdit) {
      columns.push({
        field: 'actions',
        type: 'actions',
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            key={`edit-${params.row.id}`}
            icon={<EditIcon />}
            onClick={() => {
              editProduct(params.row);
            }}
            label='Редактировать'
          />,
          <GridActionsCellItem
            key={`delete-${params.row.id}`}
            icon={<DeleteOutlineIcon />}
            onClick={() => {
              intentionallyFloatingPromiseReturn(deleteProduct)(params.row);
            }}
            label='Удалить'
          />,
        ],
      });
    }

    const editProduct = (product?: ProjectProduct): void => {
      setEditedProduct(product);
      setProductDialogOpen(true);
    };

    const handleProductDialogCancel: VoidFunction = () => {
      setProductDialogOpen(false);
    };

    const handleProductDialogSave = async (product: ProjectProduct): Promise<void> => {
      const index = !isNil(editedProduct)
        ? project.orderProducts.findIndex((p) => p.id === editedProduct.id)
        : -1;

      if (!isNil(editedProduct) && index >= 0) {
        await updateProjectSwr(async () => {
          const newProduct = await orderProjectService.updateProductAsync(
            orderId,
            project.id,
            product.id,
            product
          );

          const request = projectUtils.replaceProduct(
            cloneDeep(project),
            newProduct.id,
            newProduct
          );

          if (isNil(request)) {
            throw new Error('Не удалось обновить товар');
          }

          return request;
        });
      } else {
        await addProducts([product]);
      }

      setProductDialogOpen(false);
    };

    const updateProject = async (
      data: () => CreateOrUpdateOrderProjectRequest
    ): Promise<Array<OrderProject[] | undefined>> =>
      await updateProjectSwr(
        async () =>
          await orderProjectService.updateOrderProjectAsync(orderId, project.id, data())
      );

    const handleAddProduct = (): void => {
      const maxPositionIndex =
        project.orderProducts.length > 0
          ? Math.max(...project.orderProducts.map((p) => p.positionIndex))
          : -1;

      editProduct({
        id: -Date.now(),
        count: 1,
        name: '',
        price: 0,
        sum: 0,
        totalPrice: 0,
        totalSum: 0,
        measure: MeasureEnum.Piece,
        remark: '',
        positionIndex: maxPositionIndex + 1,
        markups: [],
        tags: [],
      });
    };

    const addProducts = async (newProducts: ProjectProduct[]): Promise<void> => {
      const maxPositionIndex =
        project.orderProducts.length > 0
          ? Math.max(...project.orderProducts.map((p) => p.positionIndex))
          : -1;

      await updateProjectSwr(async () => {
        const request = await Promise.all(
          newProducts.map(
            async (p, i) =>
              await orderProjectService.createProductAsync(orderId, project.id, {
                ...p,
                id: 0,
                positionIndex: maxPositionIndex + 1 + i,
              })
          )
        );

        const newProject = projectUtils.addProducts(cloneDeep(project), request);

        if (isNil(newProject)) {
          throw new Error('Не удалось добавить товар');
        }

        return newProject;
      });

      setPaginationModel({ ...paginationModel, page: 0 });
    };

    const productsSelectCancelHandler: VoidFunction = () => {
      setOpenProductSelectDialog(false);
    };

    const productsSelectSaveHandler = async (
      selectedProducts: Product[]
    ): Promise<void> => {
      const maxPositionIndex =
        project.orderProducts.length > 0
          ? Math.max(...project.orderProducts.map((p) => p.positionIndex))
          : -1;

      const newProducts: ProjectProduct[] = selectedProducts.map((p, i) => {
        const defaultCount = p.measure === MeasureEnum.Piece ? 1 : 0;

        return {
          count: defaultCount,
          measure: p.measure,
          name: p.name,
          price: p.price,
          totalPrice: p.price,
          sum: defaultCount * p.price,
          totalSum: defaultCount * p.price,
          id: -(Date.now() + i),
          productId: p.id,
          remark: '',
          positionIndex: maxPositionIndex + 1 + i,
          markups: [],
          tags: p.tags,
        };
      });

      setOpenProductSelectDialog(false);

      if (productSelectDialogMode === ProductSelectModeEnum.Single) {
        editProduct(first(newProducts));
      } else {
        await addProducts(newProducts);
      }

      sendProductSelectDialogModeToMetrika(productSelectDialogMode);
    };

    const sendProductSelectDialogModeToMetrika = (mode: ProductSelectModeEnum): void => {
      if (process.env.NODE_ENV !== 'production' || !process.env.REACT_APP_METRIKA_ACCOUNT)
        return;

      const ymIds = [
        '71cbd0c0-513a-4c33-8702-a6f3f1893ce5',
        '3808d152-dae1-4e58-9c6b-d01b9727bbee',
      ] as const;
      ym(+process.env.REACT_APP_METRIKA_ACCOUNT, 'reachGoal', ymIds[mode]);
    };

    const addSingleFromProductsDialogHandler = (): void => {
      setProductSelectDialogMode(ProductSelectModeEnum.Single);
      setOpenProductSelectDialog(true);
    };

    const addManyFromProductsDialogHandler = (): void => {
      setProductSelectDialogMode(ProductSelectModeEnum.Multiple);
      setOpenProductSelectDialog(true);
    };

    const addProductOptions = [
      { label: 'Добавить из справочника', action: addSingleFromProductsDialogHandler },
      { label: 'Массовое добавление', action: addManyFromProductsDialogHandler },
    ];

    const copyClickHandler = (): void => {
      !isNil(onCopy) && onCopy(project);
      handleMenuClose();
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
      setMenuAnchorEl(event.currentTarget);
    };

    const handleMenuClose = (): void => {
      setMenuAnchorEl(null);
    };

    const renameClickHandler = (): void => {
      setRenameDialogOpen(true);
      handleMenuClose();
    };

    const handleOnCloseRenameDialog = (): void => {
      setRenameDialogOpen(false);
    };

    const handleOnSaveRenameDialog = (value: string): void => {
      setRenameLoading(true);

      updateProject(() => {
        const request = cloneDeep(project);
        request.name = value;

        return request;
      })
        .then((p) => {
          setRenameDialogOpen(false);
          setRenameLoading(false);

          return p;
        })
        .catch((e) => {
          setRenameLoading(false);
          console.log(e);
        });
    };

    const activeChangeHandler = (value: boolean): void => {
      updateProject(() => {
        const request = cloneDeep(project);
        request.active = value;

        return request;
      })
        .then((p) => {
          setRenameDialogOpen(false);

          return p;
        })
        .catch(console.log);
    };

    const mobileProductList = sortedProducts.length ? (
      <>
        {sortedProducts.map((product, index) => {
          const markup = projectUtils.getTotalMarkupForUser(
            [product],
            userStore.userData?.id ?? 0
          );

          return (
            <Card variant='outlined' key={product.id}>
              <CardContent>
                <Stack direction='row' alignItems='center' gap={1}>
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    fontWeight={600}
                    sx={{ minWidth: 24 }}>
                    {index + 1}
                  </Typography>
                  <Typography variant='body1'>{product.name}</Typography>
                  <ProductTags tags={product.tags} />
                </Stack>

                <Typography variant='body2' color='text.secondary' mt={1}>
                  {`Количество: ${product.count} ${getMeasureName(
                    product.measure,
                    true
                  )}, Цена: ${currencyFormatter.format(
                    product.price
                  )}, Наценка: ${currencyFormatter.format(markup.total)}${
                    markup.user > 0 ? ` (${currencyFormatter.format(markup.user)})` : ''
                  }, Стоимость: ${currencyFormatter.format(product.totalSum)}`}
                </Typography>
              </CardContent>
              {canEdit && (
                <CardActions sx={{ pt: 0 }}>
                  <IconButton
                    aria-label='Редактировать товар'
                    size='small'
                    onClick={() => {
                      editProduct(product);
                    }}>
                    <ModeEditIcon fontSize='small' />
                  </IconButton>
                  <IconButton
                    aria-label='Удалить товар'
                    size='small'
                    color='warning'
                    onClick={() => {
                      intentionallyFloatingPromiseReturn(deleteProduct)(product);
                    }}>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </CardActions>
              )}
            </Card>
          );
        })}
        <Total
          totalSum={getTotalSum(project.orderProducts ?? [])}
          markup={projectUtils.getTotalMarkupForUser(
            project.orderProducts ?? [],
            userStore.userData?.id ?? 0
          )}
        />
      </>
    ) : (
      <EmptyMessage />
    );

    return (
      <Stack direction={'column'} spacing={2}>
        <Stack gap={1}>
          <Stack direction={'row'} alignItems={'center'}>
            <Typography variant='h6' sx={{ overflowWrap: 'break-word', minWidth: 0 }}>
              {project.name}
            </Typography>

            <IconButton
              id='project-menu-button'
              aria-label='menu'
              size='small'
              onClick={handleMenuClick}
              aria-controls={openMenu ? 'project-menu' : undefined}
              aria-haspopup='true'
              aria-expanded={openMenu ? 'true' : undefined}>
              <MoreVertIcon fontSize='inherit' />
            </IconButton>
            <Menu
              id='project-menu'
              anchorEl={menuAnchorEl}
              open={openMenu}
              onClose={handleMenuClose}
              MenuListProps={{
                'aria-labelledby': 'project-menu-button',
              }}>
              {canEdit && <MenuItem onClick={renameClickHandler}>Переименовать</MenuItem>}
              <MenuItem onClick={copyClickHandler}>Создать копию</MenuItem>

              {!project.active && (
                <MenuItem
                  onClick={() => {
                    activeChangeHandler(true);
                  }}>
                  Сделать активным
                </MenuItem>
              )}

              {project.active && (
                <MenuItem
                  onClick={() => {
                    activeChangeHandler(false);
                  }}>
                  Сделать неактивным
                </MenuItem>
              )}
            </Menu>

            {!canEdit && (
              <Chip
                label='Только просмотр'
                color='primary'
                size='small'
                variant='outlined'
              />
            )}
          </Stack>

          <Typography variant='caption' component={'div'}>
            {dateTimeFormatter.format(project.dateCreate)}
            {!isNil(project.user) && `, ${project.user.fullName}`}
          </Typography>
        </Stack>

        <Stack direction={'row'} gap={2} alignItems='center' flexWrap={'wrap'}>
          {canEdit && (
            <>
              <Button
                variant='outlined'
                onClick={handleAddProduct}
                sx={{ flex: isSmScreen ? 1 : null }}>
                Добавить товар
              </Button>

              <SplitButton
                sx={{ flex: isSmScreen ? 1 : null }}
                id='add-order-product-split-button'
                options={addProductOptions}
                color='secondary'
              />

              <Tooltip title='Настроить порядок товаров для печати'>
                <Button
                  sx={{ flex: isSmScreen ? 'auto' : null }}
                  variant='outlined'
                  color='inherit'
                  startIcon={<SortIcon />}
                  onClick={() => {
                    setProductOrderDialogOpen(true);
                  }}>
                  Порядок печати
                </Button>
              </Tooltip>
            </>
          )}
        </Stack>

        {isSmScreen ? (
          mobileProductList
        ) : (
          <DataGrid
            sx={{
              [`&.${gridClasses['root--densityStandard']} .${gridClasses.cell}`]: {
                py: 2,
              },
              [`& .${gridClasses.cell}:focus`]: {
                outline: 'none',
              },
            }}
            rows={sortedProducts}
            columns={columns}
            pagination
            disableRowSelectionOnClick
            getRowHeight={() => 'auto'}
            autoHeight
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            slots={{
              footer: () => <ProductsGridFooter products={project.orderProducts} />,
            }}
          />
        )}

        <ProductOrderDialog
          open={productOrderDialogOpen}
          products={project.orderProducts}
          onClose={() => {
            setProductOrderDialogOpen(false);
          }}
          onSave={handleSaveProductOrder}
        />

        <ProductSelectDialog
          open={openProductSelectDialog}
          mode={productSelectDialogMode}
          onCancel={productsSelectCancelHandler}
          onSave={intentionallyFloatingPromiseReturn(productsSelectSaveHandler)}
        />

        {editedProduct && (
          <ProductEditDialog
            open={productDialogOpen}
            product={editedProduct}
            onCancel={handleProductDialogCancel}
            onSave={intentionallyFloatingPromiseReturn(handleProductDialogSave)}
          />
        )}

        <PromptDialog
          open={renameDialogOpen}
          defaultValue={project.name}
          maxWidth={'xs'}
          fullWidth
          title={'Введите название проекта'}
          saveButtonProps={{ component: LoadingButton, loading: renameLoading }}
          onClose={handleOnCloseRenameDialog}
          onSave={handleOnSaveRenameDialog}
        />
      </Stack>
    );
  }
);

export interface OrderProjectEditParams {
  orderId: number;
  projects: OrderProject[];
  project: OrderProject;
  onCopy?: (project: OrderProject) => void;
}

export default OrderProjectEdit;
